import { useEffect, useState } from 'react';
import { ArrowRight, Target, Users, Activity, Clock } from 'lucide-react';
import MarketingLayout from '../components/MarketingLayout';
import PageHero from '../components/PageHero';

export default function GBCTransitionPage() {
  const [countdown, setCountdown] = useState(30);

  // Countdown timer and auto-redirect
  useEffect(() => {
    // Set page meta tags
    document.title = 'Global Business Consultants is now StratPlan';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Global Business Consultants is now StratPlan. Bring your company’s direction, people and execution into one guided strategic planning workspace.'
      );
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content =
        'Global Business Consultants is now StratPlan. Bring your company’s direction, people and execution into one guided strategic planning workspace.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add canonical URL
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = 'https://stratplan.co/gbc-transition';
    document.getElementsByTagName('head')[0].appendChild(canonical);

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = 'https://stratplan.co';
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      // Cleanup meta tags
      const metaToRemove = document.querySelector('meta[name="description"]');
      const canonicalToRemove = document.querySelector('link[rel="canonical"]');
      if (metaToRemove) metaToRemove.remove();
      if (canonicalToRemove) canonicalToRemove.remove();
    };
  }, []);

  const handleRedirect = () => {
    window.location.href = 'https://stratplan.co';
  };

  return (
    <MarketingLayout>
      <PageHero
        badge={<span>Formerly Global Business Consultants</span>}
        title={
          <>
            A new home for
            <br />
            <span className="text-blue-600">your next strategic move.</span>
          </>
        }
        subtitle="Global Business Consultants is now StratPlan. Continue your planning journey in a connected workspace for your company’s direction, business analysis, priorities and execution."
      >
        <button onClick={handleRedirect} className="public-button public-button--primary">
          Continue to StratPlan <ArrowRight className="h-5 w-5" />
        </button>
        <a href="https://stratplan.co/contact" className="public-button public-button--secondary">
          Contact our team
        </a>
      </PageHero>
      <section className="public-section">
        <div className="public-container">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <p className="public-eyebrow">From strategic thinking to a practical plan</p>
            <h2 className="mb-5">Bring the direction and the work together.</h2>
            <p className="text-xl">
              StratPlan combines guided strategic frameworks with a shared view of your priorities,
              people and projects. AI assistance helps you draft and refine the next step using the
              company context you provide.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Target,
                title: 'Build your strategic foundation',
                description:
                  'Define vision, mission, values and objectives, then assess the market and your capabilities.',
              },
              {
                icon: Users,
                title: 'Make ownership clear',
                description:
                  'Map your organization, teams and positions so responsibilities have a visible place in the plan.',
              },
              {
                icon: Activity,
                title: 'Connect strategy to action',
                description:
                  'Follow selected strategies into projects and tasks, review progress and present your plan as a visual report.',
              },
            ].map(({ icon: Icon, title, description }) => (
              <article key={title} className="public-card p-7">
                <Icon className="h-8 w-8 text-blue-600 mb-5" />
                <h3 className="mb-3">{title}</h3>
                <p className="text-gray-600">{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="public-cta">
        <div className="public-container text-center">
          <h2 className="mb-4">Your planning journey continues here.</h2>
          <p className="max-w-2xl mx-auto mb-7">
            Head to StratPlan to explore the platform and choose your next step.
          </p>
          <button onClick={handleRedirect} className="public-button public-button--primary">
            Open StratPlan <ArrowRight className="h-5 w-5" />
          </button>
          <p
            className="mt-6 text-sm flex flex-wrap items-center justify-center gap-2"
            role="status"
          >
            <Clock className="h-4 w-4" />
            Continuing automatically to{' '}
            <a href="https://stratplan.co" className="text-blue-600">
              stratplan.co
            </a>{' '}
            in {countdown} seconds.
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}
